import type { SignInFlowConfig } from "./flow";

/** The copy/behaviour subset of the flow config a variant may set. */
export type SignInVariant = Pick<
  SignInFlowConfig,
  "headings" | "emailCta" | "submitCta" | "createCta" | "intro" | "showConsent"
>;

/** Named copy/behaviour bundles. Adding a context is a new entry here — data,
 * not code. `default` is empty: the flow's built-in copy is the default. */
export const VARIANTS: Record<string, SignInVariant> = {
  default: {},
  checkout: {
    // Placeholder copy — final wording owned by design (Oscar) / Ro. Kept
    // complementary to the entry heading rather than echoing it.
    intro: "You'll continue to checkout once you're signed in.",
    headings: { registration: "Your details" },
    createCta: "Continue to checkout",
    showConsent: false,
  },
  // Placeholder copy — final wording owned by design (Oscar) / Ro. One bundle
  // per wishlist threshold (first save vs. later saves).
  "wishlist-first": {
    headings: { entry: "You've just saved a trip!" },
    intro: "Keep your list forever with an account",
  },
  "wishlist-third": {
    headings: { entry: "Don't lose your list" },
    intro: "Sign up for an account",
  },
};

/** Own-property keys only: `VARIANTS["toString"]` would otherwise resolve
 * through the prototype chain and count as a known variant. */
const KNOWN = new Set(Object.keys(VARIANTS));

/** Untrusted key -> a known bundle, else the default. Never throws, never
 * echoes the requested key (this renders on the identity origin). */
export function resolveVariant(key?: string | null): SignInVariant {
  return (key && KNOWN.has(key) && VARIANTS[key]) || {};
}

/** Untrusted key -> the same key if it names a real bundle, else undefined.
 * The gate for putting a variant back on the wire (the social-auth begin URL,
 * and from there the edge's hand-off cookie): an unknown key resolves to the
 * default copy anyway, so there is nothing to carry and no reason to reflect
 * it. `default` is that implicit fallback, so it carries nothing either. */
export function knownVariantKey(key?: string | null): string | undefined {
  return key && key !== "default" && KNOWN.has(key) ? key : undefined;
}
